/**
 * formatters.ts
 *
 * Three public concepts:
 *
 *   1. Standalone one-offs  — formatMoney, formatNumber, formatInteger, formatPercentage, formatDuration
 *      Opinionated defaults for single-value use (tooltips, headers, one-off labels).
 *      All accept an optional FormatOptions for rare overrides (e.g. axis ticks need removeTrailingZeroes).
 *
 *   2. Axis tick helpers    — createYAxisFormatterFromTicks, getYAxisFormatter
 *      Build a formatter scoped to a set of tick values so every tick on an axis uses the same scale/unit.
 *      Apply axis-specific defaults (trailing-zero removal, sci notation for tiny values).
 *
 *   3. Domain-aware factory — createFormatterForValues(values, dataType, options?)
 *      THE way to format a set of related values (e.g. an entire table column) consistently.
 *      Auto-detects scale/unit/decimal places from the data — no options needed.
 *      Pass FormatOptions (fixedDecimals, currencyCode) to override auto-detection.
 *      Designed to be called once; the returned formatter is reused for every value in the set.
 *
 * Implementation helpers (getScaleForDomain, formatMoneyWithScale, etc.) are intentionally private.
 * If you find yourself wanting to reach for one of them, you probably want createFormatterForValues.
 */

import { format } from '@visx/vendor/d3-format';

// =============================================================================
// 1. TYPES & CONSTANTS
// =============================================================================

/**
 * The data types supported by domain-aware formatters.
 *
 * - 'number'     Plain floats; 1 decimal place for alignment, trailing zeros stripped (e.g. "12.3", "42")
 * - 'integer'    Whole-number counts, no decimals (e.g. generations, errors)
 * - 'money'      Currency values; 2 dp for regular range, 3 sig figs for tiny values, K/M/B for large
 * - 'percentage' Values stored as 0-1, displayed as 0-100% (e.g. 0.857 → "85.7%")
 * - 'time'       Durations in milliseconds, auto-scaled to ms/s/m/h/d/y
 */
export type DataType = 'number' | 'integer' | 'money' | 'percentage' | 'time';

/**
 * ISO 4217 currency codes supported by formatMoney.
 */
export type CurrencyCode = 'USD' | 'EUR' | 'GBP';

/** Runtime list for validation/iteration if needed. */
export const SUPPORTED_CURRENCY_CODES: readonly CurrencyCode[] = ['USD', 'EUR', 'GBP'] as const;

/**
 * Options accepted by standalone one-off formatters.
 *
 * These are intentionally rare — most callers should never pass options.
 * The main consumer of both flags is the axis tick path (see getYAxisFormatter).
 */
export interface FormatOptions {
  /**
   * Use scientific notation for very small values (< 0.001).
   * Saves horizontal space on axis labels.
   * @default false
   */
  useSciNotationForSmallValues?: boolean;

  /**
   * Strip trailing zeros from formatted values (e.g. "1.0K" → "1K").
   * Defaults to `true` for formatNumber and formatDuration (clean standalone display).
   * Defaults to `false` for formatMoney and formatPercentage (preserves meaningful zeros).
   * Pass `false` explicitly to force a fixed decimal place for alignment.
   */
  removeTrailingZeroes?: boolean;

  /**
   * Currency code for money formatting. Determines the symbol shown (e.g. €, £).
   * @default 'USD'
   */
  currencyCode?: CurrencyCode;

  /**
   * Force an exact number of decimal places, bypassing all auto-detection.
   * When set on formatMoney, also bypasses K/M/B abbreviation.
   * When set on createFormatterForValues, overrides the domain-wide scale/precision.
   * When the value would round to zero, displays "< 0.01" (or equivalent) instead.
   * @default undefined (auto-detect)
   */
  fixedDecimals?: number;

  /**
   * Pre-computed currency symbol. Internal use only — avoids recreating
   * Intl.NumberFormat per value in hot paths (e.g. formatMoneyWithScale).
   */
  symbol?: string;
}

// Scale thresholds: when the domain max exceeds these, ALL values in the set use that suffix.
// Keeping one threshold definition here prevents drift if we ever adjust them.
const SCALE_THRESHOLD_K = 1500;
const SCALE_THRESHOLD_M = 1_000_000;
const SCALE_THRESHOLD_B = 1_000_000_000;

// Time unit boundaries (all in milliseconds).
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const MS_PER_YEAR = 365 * MS_PER_DAY;

/**
 * The time unit used for domain-aware duration formatting.
 * All values in a set share the same unit so comparison is easy.
 */
export type TimeUnit = 'ms' | 's' | 'm' | 'h' | 'd' | 'y';

/**
 * Granularity inference for X-axis time labels.
 */
export type TimeGranularity = 'hourly' | 'daily' | 'monthly';

// Shared d3 format strings — centralised so precision doesn't drift.
const FMT_NUMBER_ABBREV = '.3s'; // e.g. 1.23K
const FMT_NUMBER_1DEC_ABBREV = '.2s'; // e.g. 1.2K

// Default options baked into axis tick formatters.
// NOT exported — callers should use createYAxisFormatterFromTicks / getYAxisFormatter.
const AXIS_TICK_OPTIONS: FormatOptions = {
  useSciNotationForSmallValues: true,
  removeTrailingZeroes: true,
};

// Edge-case bounds: below/above these, use scientific notation or clamp to avoid overflow.
const MIN_SAFE_MAGNITUDE = 1e-100;
const MAX_SAFE_MAGNITUDE = 1e100;
const MAX_SAFE_DECIMAL_PLACES = 20;
const SCI_NOTATION_THRESHOLD = 0.001;

// =============================================================================
// 2. PRIVATE HELPERS
// =============================================================================

/** Returns true if value is outside "normal" range for formatting (use sci notation or clamp). */
function isExtremeValue(value: number): boolean {
  return (
    !isFinite(value) || Math.abs(value) >= MAX_SAFE_MAGNITUDE || (value !== 0 && Math.abs(value) < MIN_SAFE_MAGNITUDE)
  );
}

/** Unicode minus for negative values; empty string for non-negative. */
function formatSign(value: number): string {
  return value < 0 ? '\u2212' : '';
}

/** Clamp decimal places to avoid toFixed/format overflow. */
function clampDecimalPlaces(dp: number): number {
  return Math.min(Math.max(0, dp), MAX_SAFE_DECIMAL_PLACES);
}

/** d3 uses lowercase 'k' and 'G' (giga); normalise to business-friendly K/B. */
function normalizeSIPrefixes(s: string): string {
  return s.replace(/k\b/g, 'K').replace(/G\b/g, 'B');
}

/**
 * Inserts d3's tilde (~) trailing-zero-removal modifier before the final
 * format character. Works for any d3 format string.
 * e.g. '.3s' → '.3~s',  '$.3s' → '$.3~s'
 */
function withTrailingZeroRemoval(fmt: string, remove: boolean): string {
  return remove ? fmt.replace(/([a-z])$/i, '~$1') : fmt;
}

/** Returns true when a formatted string is zero (handles Unicode minus). */
function isFormattedZero(s: string): boolean {
  return /^[\u2212-]?0(?:\.0+)?$/.test(s);
}

/**
 * Returns true when value is non-zero but below the minimum representable with fixedDecimals.
 * Used to show "< 0.01" (or equivalent) instead of misleading "0.00" when value would round to zero.
 */
function isBelowMinimumRepresentable(value: number, fixedDecimals: number): boolean {
  if (value === 0) {
    return false;
  }
  const dp = clampDecimalPlaces(fixedDecimals);
  const min = Math.pow(10, -dp);
  return Math.abs(value) < min;
}

// --- Scale helpers ---

type Scale = 'B' | 'M' | 'K' | null;

function getScaleForDomain(maxValue: number): Scale {
  const abs = Math.abs(maxValue);
  if (abs >= SCALE_THRESHOLD_B) {
    return 'B';
  }
  if (abs >= SCALE_THRESHOLD_M) {
    return 'M';
  }
  if (abs > SCALE_THRESHOLD_K) {
    return 'K';
  }
  return null;
}

function getScaleDivisor(scale: Scale): number {
  if (scale === 'B') {
    return 1_000_000_000;
  }
  if (scale === 'M') {
    return 1_000_000;
  }
  if (scale === 'K') {
    return 1_000;
  }
  return 1;
}

// --- Time helpers ---

function getTimeUnitForDomain(maxMs: number): TimeUnit {
  const abs = Math.abs(maxMs);
  if (abs >= MS_PER_YEAR) {
    return 'y';
  }
  if (abs >= MS_PER_DAY) {
    return 'd';
  }
  if (abs >= MS_PER_HOUR) {
    return 'h';
  }
  if (abs >= MS_PER_MINUTE) {
    return 'm';
  }
  // Only switch to seconds when max >= 2s so that e.g. 800ms, 1000ms, 1200ms
  // stay in "ms" rather than becoming "0.8s", "1s", "1.2s".
  if (abs >= 2 * MS_PER_SECOND) {
    return 's';
  }
  return 'ms';
}

function getTimeUnitDivisor(unit: TimeUnit): number {
  if (unit === 'y') {
    return MS_PER_YEAR;
  }
  if (unit === 'd') {
    return MS_PER_DAY;
  }
  if (unit === 'h') {
    return MS_PER_HOUR;
  }
  if (unit === 'm') {
    return MS_PER_MINUTE;
  }
  if (unit === 's') {
    return MS_PER_SECOND;
  }
  return 1;
}

// --- Money helpers ---

/** Returns the narrow currency symbol for the given ISO 4217 code (e.g. "$", "€", "£"). */
function getCurrencySymbol(currencyCode: CurrencyCode = 'USD'): string {
  return (
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(1)
      .find((p) => p.type === 'currency')?.value ?? '$'
  );
}

/** Rounds value to n significant figures. Used to cap money at 3 sig figs. */
function roundToSigFigs(value: number, sigFigs: number): number {
  if (value === 0 || !isFinite(value)) {
    return value;
  }
  if (isExtremeValue(value)) {
    return 0;
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? -1 : 1;
  const magnitude = Math.floor(Math.log10(abs));
  const scale = Math.pow(10, sigFigs - 1 - magnitude);
  if (!isFinite(scale) || scale === 0) {
    return 0;
  }
  return (sign * Math.round(abs * scale)) / scale;
}

/**
 * Decimal places needed to represent `value` to `sigFigs` significant figures,
 * with trailing zeros already removed. For example:
 *   0.001    → 3 (1 sig fig at position 3)
 *   0.0033   → 4 (2 sig figs, last at position 4)
 *   0.00123  → 5 (3 sig figs, last at position 5)
 *   12.34    → 1 (3 sig figs = "12.3", 1 dp)
 *   100      → 0 (3 sig figs = "100", 0 dp)
 *
 * Used for both the "never round to zero" fix in standalone formatters and
 * the column-precision calculation in createFormatterForValues.
 */
function naturalDecimalPlaces(value: number, sigFigs: number = 3): number {
  const abs = Math.abs(value);
  if (abs === 0) {
    return 0;
  }
  const prec = abs.toPrecision(sigFigs);
  let dp: number;
  if (prec.includes('e')) {
    // Very small values (< 1e-6) return scientific notation from toPrecision.
    // Compute dp directly: all sigFigs digits appear after the decimal point.
    dp = Math.max(0, -Math.floor(Math.log10(abs)) + sigFigs - 1);
  } else {
    const cleaned = prec.replace(/\.?0+$/, '');
    const dotIdx = cleaned.indexOf('.');
    dp = dotIdx >= 0 ? cleaned.length - dotIdx - 1 : 0;
  }
  return clampDecimalPlaces(dp);
}

// Maximum decimal places for money formatting in domain-aware columns.
// Keeps cost values readable (e.g. $0.0004 instead of $0.000421) while
// still showing meaningful data for sub-cent values.
const MAX_MONEY_DECIMAL_PLACES = 4;

/**
 * Determines the decimal precision to apply uniformly to a set of values.
 * Uses the smallest value (by magnitude): express it to 3 significant figures
 * (trailing zeros removed), count decimal places, and use that for the column.
 * All values then share the same number of figures after the decimal point.
 * Capped at MAX_MONEY_DECIMAL_PLACES to avoid excessive precision.
 *
 * Examples (smallest value → 3 sig figs → dp, capped at 4):
 *   0.0610 → 0.061 → 3 dp
 *   0.00520 → 0.0052 → 4 dp
 *   0.00606 → 0.00606 → 5 dp → capped to 4 dp
 *   0.005051 → 0.00505 → 5 dp → capped to 4 dp
 *   0.00070701 → 0.000707 → 6 dp → capped to 4 dp
 */
function decimalPrecisionForSet(values: Array<number | null | undefined>): number {
  const nonZero = values.filter((v): v is number => v !== null && v !== undefined && !isNaN(v) && v !== 0);
  if (nonZero.length === 0) {
    return 2;
  }
  const smallest = nonZero.reduce((a, b) => (Math.abs(a) <= Math.abs(b) ? a : b));
  return Math.min(MAX_MONEY_DECIMAL_PLACES, Math.max(2, naturalDecimalPlaces(smallest, 3)));
}

/** Format money at a fixed decimal-place precision (used for consistent columns). */
function formatMoneyFixed(value: number, decimalPlaces: number, symbol: string = '$'): string {
  if (!isFinite(value)) {
    return String(value);
  }
  if (value === 0) {
    return `${symbol}0.00`;
  }
  if (Math.abs(value) >= 1000) {
    const absFormatted = normalizeSIPrefixes(format('.3s')(Math.abs(value)));
    return `${formatSign(value)}${symbol}${absFormatted}`;
  }
  const abs = Math.abs(value);
  if (abs < MIN_SAFE_MAGNITUDE) {
    return `${formatSign(value)}${symbol}${format('.2e')(abs)}`;
  }
  // Values at or above $0.01 (1 cent): always exactly 2 decimal places.
  // This is strict regardless of the domain-derived decimal places, so $0.50 never
  // shows as $0.500 and $0.06 never loses its cent as $0.061.
  if (abs >= 0.01) {
    return `${formatSign(value)}${symbol}${format(',.2f')(abs)}`;
  }
  // Sub-cent values: use domain-wide precision (capped at MAX_MONEY_DECIMAL_PLACES).
  const rounded = roundToSigFigs(abs, 3);
  if (!isFinite(rounded) || rounded === 0) {
    return `${formatSign(value)}${symbol}${format('.2e')(abs)}`;
  }
  const safeDp = clampDecimalPlaces(decimalPlaces);
  const formatted = format(`,.${safeDp}f`)(rounded);
  // When the dp cap causes a non-zero value to display as all zeros, show "< $0.0001" instead.
  if (isFormattedZero(formatted)) {
    const min = Math.pow(10, -safeDp).toFixed(safeDp);
    return value < 0 ? `> ${formatSign(value)}${symbol}${min}` : `< ${symbol}${min}`;
  }
  return `${formatSign(value)}${symbol}${formatted}`;
}

/** Format a number value with domain scale applied (1 decimal place at the scaled unit). */
function formatNumberWithScale(value: number, scale: Scale, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  const { removeTrailingZeroes = false } = options;
  if (value === 0) {
    return removeTrailingZeroes ? '0' : '0.0';
  }
  if (scale === null) {
    // For values where 1 decimal place would round to 0.0 (i.e. |value| < 0.05),
    // use enough decimal places to show 3 significant figures instead.
    if (Math.abs(value) < 0.05) {
      const dp = naturalDecimalPlaces(value, 3);
      return format(`,.${dp}${removeTrailingZeroes ? '~' : ''}f`)(value);
    }
    return removeTrailingZeroes ? format(',.1~f')(value) : format(',.1f')(value);
  }
  const divisor = getScaleDivisor(scale);
  const scaled = value / divisor;
  const oneDecFmt = removeTrailingZeroes ? format('.1~f')(scaled) : format('.1f')(scaled);
  if (isFormattedZero(oneDecFmt)) {
    return `${format('.3~g')(scaled)}${scale}`;
  }
  return `${oneDecFmt}${scale}`;
}

/** Format a money value with domain scale applied (1 decimal place at the scaled unit). */
function formatMoneyWithScale(value: number, scale: Scale, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  const {
    useSciNotationForSmallValues = false,
    removeTrailingZeroes = false,
    currencyCode,
    symbol: precomputedSymbol,
  } = options;
  const symbol = precomputedSymbol ?? getCurrencySymbol(currencyCode);
  if (value === 0) {
    return removeTrailingZeroes ? `${symbol}0` : `${symbol}0.00`;
  }
  if (useSciNotationForSmallValues && Math.abs(value) < SCI_NOTATION_THRESHOLD) {
    return `${formatSign(value)}${symbol}${format('.2e')(Math.abs(value))}`;
  }
  if (scale === null) {
    // No K/M/B abbreviation: show up to 2 decimal places.
    // removeTrailingZeroes=true (axis path) → compact tick labels ("$100" not "$100.00").
    // removeTrailingZeroes=false (domain path) → 2dp kept for column alignment.
    const absFormatted = format(`,.2${removeTrailingZeroes ? '~' : ''}f`)(Math.abs(value));
    return `${formatSign(value)}${symbol}${absFormatted}`;
  }
  const divisor = getScaleDivisor(scale);
  const absScaled = Math.abs(value) / divisor;
  const sign = formatSign(value);
  const oneDecFmt = removeTrailingZeroes ? format('.1~f')(absScaled) : format('.1f')(absScaled);
  if (isFormattedZero(oneDecFmt)) {
    return `${sign}${symbol}${format('.3~g')(absScaled)}${scale}`;
  }
  return `${sign}${symbol}${oneDecFmt}${scale}`;
}

/** Format an integer with domain scale applied (no decimals unless scaled). */
function formatIntegerWithScale(value: number, scale: Scale): string {
  if (!isFinite(value)) {
    return String(value);
  }
  if (value === 0) {
    return '0';
  }
  if (scale === null) {
    return format(',d')(Math.round(value));
  }
  const divisor = getScaleDivisor(scale);
  const scaled = value / divisor;
  const oneDecFmt = format('.1~f')(scaled);
  if (isFormattedZero(oneDecFmt)) {
    return `${format('.3~g')(scaled)}${scale}`;
  }
  return `${oneDecFmt}${scale}`;
}

// --- Percentage helpers ---

/**
 * Returns true when any value in `pctValues` (0-100 range) has a non-zero
 * fractional part when rounded to 1 decimal place.
 * Used by createFormatterForValues and createYAxisFormatterFromTicks to decide
 * whether to show decimals on ALL values for consistency.
 */
function hasAnyNonZeroDecimal(pctValues: number[]): boolean {
  for (const v of pctValues) {
    if (!isFinite(v) || Math.abs(v) >= MAX_SAFE_MAGNITUDE) {
      continue;
    }
    const abs = Math.abs(v);
    if (abs >= 10000) {
      // Scientific notation range — check mantissa
      const formatted = format('.1e')(v).replace(/e\+/, 'e');
      const match = formatted.match(/^([\u2212-]?\d+\.\d+)e/);
      if (match && !match[1].endsWith('.0')) {
        return true;
      }
      continue;
    }
    if (!format('.1f')(v).match(/\.0+$/)) {
      return true;
    }
  }
  return false;
}

/**
 * Forces exactly 1 decimal place on a percentage value (0-100 range).
 * Used internally when createFormatterForValues detects that some values in
 * the set have non-zero decimals and all must align.
 * Handles zero and scientific notation consistently.
 */
function formatPercentageFixed1(value: number): string {
  if (!isFinite(value)) {
    return String(value);
  }
  if (value === 0) {
    return '0.0%';
  }
  if (Math.abs(value) >= 10000) {
    // Scientific notation: force 1 decimal in mantissa for consistency.
    // IMPORTANT: Space before % for scientific notation — do NOT remove.
    const result = format('.1e')(value).replace(/e\+/, 'e');
    return `${result} %`;
  }
  return `${format('.1f')(value)}%`;
}

// =============================================================================
// 3. STANDALONE ONE-OFF FORMATTERS
// =============================================================================

/**
 * Format a currency value.
 *
 * Standalone behaviour (no options needed):
 * - Zero → "$0.00"
 * - Regular range ($0.01–$999.99) → always 2 decimal places ("$12.34", "$1.23", "$100.00")
 * - Tiny values (< $0.01) → 3 sig figs, trailing zeros preserved ("$0.000500", "$0.0000150")
 * - Large values (≥ $1000) → K/M/B abbreviation, trailing zeros preserved ("$1.23K", "$1.50K", "$1.00M")
 *
 * Options:
 * - `currencyCode` — ISO 4217 code for the currency symbol (default: USD → "$")
 * - `fixedDecimals` — exact decimal places via Intl.NumberFormat; bypasses sig-fig rule and abbreviation
 * - `removeTrailingZeroes` — strips trailing zeros from K/M/B values; mainly for axis ticks
 *
 * @example
 * formatMoney(12.34)                                          // "$12.34"
 * formatMoney(100)                                            // "$100.00"
 * formatMoney(12.34, { currencyCode: 'EUR' })      // "€12.34"
 * formatMoney(1234,  { fixedDecimals: 2, currencyCode: 'GBP' }) // "£1,234.00"
 * formatMoney(0)                                              // "$0.00"
 */
export function formatMoney(value: number, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  const { useSciNotationForSmallValues = false, removeTrailingZeroes = false, currencyCode, fixedDecimals } = options;

  // fixedDecimals path: exact precision via Intl, no abbreviation, correct symbol automatically.
  // When value would round to zero, show "< $0.01" (or equivalent) instead.
  if (fixedDecimals !== undefined) {
    const dp = clampDecimalPlaces(fixedDecimals);
    if (isBelowMinimumRepresentable(value, fixedDecimals)) {
      const min = Math.pow(10, -dp);
      const symbol = getCurrencySymbol(currencyCode);
      const minStr = min.toFixed(dp);
      // Negative: value is in (-min, 0), so "> −$0.01". Positive: value is in (0, min), so "< $0.01".
      return value < 0 ? `> ${formatSign(value)}${symbol}${minStr}` : `< ${symbol}${minStr}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode ?? 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
  }

  const symbol = getCurrencySymbol(currencyCode);

  if (value === 0) {
    return removeTrailingZeroes ? `${symbol}0` : `${symbol}0.00`;
  }

  if (useSciNotationForSmallValues && Math.abs(value) < SCI_NOTATION_THRESHOLD) {
    // Keep 2 decimal places in mantissa for meaningful precision (3 sig figs)
    return `${formatSign(value)}${symbol}${format('.2e')(Math.abs(value))}`;
  }

  if (Math.abs(value) >= 1000) {
    // Abbreviated with 3 sig figs. Trailing zeros preserved by default ("$1.50K"),
    // stripped only when removeTrailingZeroes is explicitly requested ("$1.5K").
    const absFormatted = normalizeSIPrefixes(format(removeTrailingZeroes ? '.3~s' : '.3s')(Math.abs(value)));
    return `${formatSign(value)}${symbol}${absFormatted}`;
  }

  const abs = Math.abs(value);

  if (abs < 0.01) {
    // Tiny values: cap at 3 sig figs (e.g. 0.001548 → $0.00155, not $0.001548).
    // Below MIN_SAFE_MAGNITUDE or roundToSigFigs non-finite → use scientific notation
    if (abs < MIN_SAFE_MAGNITUDE) {
      return `${formatSign(value)}${symbol}${format('.2e')(abs)}`;
    }
    const rounded = roundToSigFigs(abs, 3);
    if (!isFinite(rounded) || rounded === 0) {
      return `${formatSign(value)}${symbol}${format('.2e')(abs)}`;
    }
    const magnitude = Math.floor(Math.log10(rounded));
    const dp = clampDecimalPlaces(Math.max(2, -magnitude + 2));
    return `${formatSign(value)}${symbol}${rounded.toFixed(dp)}`;
  }

  // Regular range ($0.01–$999.99): always 2 decimal places (cents).
  // e.g. $12.34, $1.23, $100.00
  return `${formatSign(value)}${symbol}${format(',.2f')(abs)}`;
}

/**
 * Format a plain number with up to 1 decimal place.
 * Trailing zeros are stripped by default for clean display ("42" not "42.0").
 * Abbreviates large values (K, M, B).
 *
 * Pass `removeTrailingZeroes: false` to force 1 decimal place — useful when
 * you need alignment with other numbers but don't want the full domain-aware
 * factory. Prefer createFormatterForValues for column/chart use.
 *
 * @example
 * formatNumber(42)      // "42"
 * formatNumber(1234)    // "1.2K"
 * formatNumber(12.3)    // "12.3"
 * formatNumber(1000000) // "1M"
 */
export function formatNumber(value: number, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  if (Math.abs(value) >= MAX_SAFE_MAGNITUDE) {
    return value > 0 ? 'Infinity' : '-Infinity';
  }
  const { removeTrailingZeroes = true, fixedDecimals } = options;

  if (fixedDecimals !== undefined) {
    const dp = clampDecimalPlaces(fixedDecimals);
    if (isBelowMinimumRepresentable(value, fixedDecimals)) {
      const minStr = Math.pow(10, -dp).toFixed(dp);
      return value < 0 ? `> ${formatSign(value)}${minStr}` : `< ${minStr}`;
    }
    return format(`,.${dp}f`)(value);
  }

  if (Math.abs(value) >= 1000) {
    return normalizeSIPrefixes(format(withTrailingZeroRemoval(FMT_NUMBER_1DEC_ABBREV, removeTrailingZeroes))(value));
  }

  // For values where 1 decimal place would round to 0.0 (i.e. |value| < 0.05),
  // use enough decimal places to show the value rather than displaying zero.
  if (value !== 0 && Math.abs(value) < 0.05) {
    const dp = naturalDecimalPlaces(value, 3);
    return format(`,.${dp}${removeTrailingZeroes ? '~' : ''}f`)(value);
  }

  return removeTrailingZeroes ? format(',.1~f')(value) : format(',.1f')(value);
}

/**
 * Format a whole-number count.
 * Shows no decimals for values < 1000, abbreviates larger values.
 *
 * @example
 * formatInteger(90)       // "90"
 * formatInteger(1234)     // "1.23K"
 * formatInteger(1000000)  // "1.00M"
 */
export function formatInteger(value: number): string {
  if (!isFinite(value)) {
    return String(value);
  }
  if (Math.abs(value) >= 1000) {
    return normalizeSIPrefixes(format(FMT_NUMBER_ABBREV)(value));
  }
  return format(',d')(Math.round(value));
}

/**
 * Format a percentage value (0-100 range).
 *
 * Standalone behaviour:
 * - Whole numbers → no decimal ("75%")
 * - Non-whole numbers → 1 decimal ("85.7%")
 * - Very small values that would display as "0.0%" → meaningful precision instead ("0.0005%")
 * - |value| >= 10000 → scientific notation ("1.2e4 %")
 * - Negative values use Unicode minus (U+2212) via d3-format
 *
 * For consistent decimal places across a set of values, use createFormatterForValues
 * with dataType 'percentage' instead — it auto-detects whether decimals are needed.
 *
 * @param value  Percentage in 0-100 range (not 0-1)
 * @param options  Rarely needed overrides
 *
 * @example
 * formatPercentage(85.7)   // "85.7%"
 * formatPercentage(75)     // "75%"
 * formatPercentage(0.0005) // "0.0005%"
 * formatPercentage(12345)  // "1.2e4 %"
 */
export function formatPercentage(value: number, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  if (Math.abs(value) >= MAX_SAFE_MAGNITUDE) {
    return value > 0 ? 'Infinity' : '-Infinity';
  }
  const { useSciNotationForSmallValues = false, removeTrailingZeroes = false, fixedDecimals } = options;

  if (fixedDecimals !== undefined) {
    const dp = clampDecimalPlaces(fixedDecimals);
    if (value === 0) {
      return `${(0).toFixed(dp)}%`;
    }
    if (isBelowMinimumRepresentable(value, fixedDecimals)) {
      const minStr = Math.pow(10, -dp).toFixed(dp);
      return value < 0 ? `> ${formatSign(value)}${minStr}%` : `< ${minStr}%`;
    }
    return `${format(`.${dp}f`)(value)}%`;
  }

  if (value === 0) {
    return '0%';
  }

  // Scientific notation for very small values (axis use).
  // IMPORTANT: Always use a space before % for scientific notation (e.g. "5e-4 %").
  // Do NOT remove this space — it improves readability and is a deliberate design choice.
  if (useSciNotationForSmallValues && Math.abs(value) < SCI_NOTATION_THRESHOLD) {
    const sciFormat = removeTrailingZeroes ? '.2~e' : '.2e';
    return `${format(sciFormat)(value).replace(/e\+/, 'e')} %`;
  }

  // Scientific notation for extreme percentages (|value| >= 10000).
  // IMPORTANT: Always use a space before % for scientific notation (e.g. "1.2e4 %").
  // Do NOT remove this space — it improves readability and is a deliberate design choice.
  if (Math.abs(value) >= 10000) {
    return `${format('.1~e')(value).replace(/e\+/, 'e')} %`;
  }

  // Never display a non-zero value as 0.0% — use enough precision to show it.
  // Must run before isWhole check so values like 0.0005 get "0.0005%" not "0%".
  if (Math.abs(value) < 0.05) {
    const dp = naturalDecimalPlaces(value, 3);
    return `${format(`.${dp}${removeTrailingZeroes ? '~' : ''}f`)(value)}%`;
  }

  // Regular range
  // Use .1f-aligned check: value is "whole" if rounding to 1 decimal equals rounding to integer.
  // This matches hasAnyNonZeroDecimal (which uses format('.1f')) and tolerates floating-point
  // errors from v*100 (e.g. 0.3*100 → 30.000000000000004) that exceed Number.EPSILON.
  const isWhole = Math.round(value * 10) / 10 === Math.round(value);
  if (isWhole) {
    return `${format('.0f')(value)}%`;
  }

  if (removeTrailingZeroes) {
    return `${format('.1~f')(value)}%`;
  }
  return `${format('.1f')(value)}%`;
}

/**
 * Format a duration in milliseconds, auto-selecting the most readable unit.
 * Trailing zeros are stripped by default ("1m" not "1.0m").
 * Pass `removeTrailingZeroes: false` to force 1 decimal place.
 *
 * @example
 * formatDuration(500)     // "500ms"
 * formatDuration(1234)    // "1.2s"
 * formatDuration(60000)   // "1m"
 * formatDuration(90000)   // "1.5m"
 */
export function formatDuration(value: number, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  const safeValue = value < 0 ? 0 : value;
  const { useSciNotationForSmallValues = false, removeTrailingZeroes = true } = options;

  const fmt1dec = removeTrailingZeroes ? format('.1~f') : format('.1f');

  if (useSciNotationForSmallValues && Math.abs(safeValue) < SCI_NOTATION_THRESHOLD && safeValue !== 0) {
    return `${format('.2e')(safeValue)}ms`;
  }

  if (safeValue >= MS_PER_YEAR) {
    return `${fmt1dec(safeValue / MS_PER_YEAR)}y`;
  }
  if (safeValue >= MS_PER_DAY) {
    return `${fmt1dec(safeValue / MS_PER_DAY)}d`;
  }
  if (safeValue >= MS_PER_HOUR) {
    return `${fmt1dec(safeValue / MS_PER_HOUR)}h`;
  }
  if (safeValue >= MS_PER_MINUTE) {
    return `${fmt1dec(safeValue / MS_PER_MINUTE)}m`;
  }
  if (safeValue >= MS_PER_SECOND) {
    return `${fmt1dec(safeValue / MS_PER_SECOND)}s`;
  }
  if (safeValue >= 1) {
    return `${format(',.0f')(safeValue)}ms`;
  }

  // Sub-millisecond: use fixed-point 3 sig figs (avoids d3's .3g switching to sci notation)
  if (safeValue === 0) {
    return removeTrailingZeroes ? '0ms' : '0.00ms';
  }
  if (safeValue <= MIN_SAFE_MAGNITUDE) {
    return `${format('.2e')(safeValue)}ms`;
  }
  const magnitude = Math.floor(Math.log10(Math.abs(safeValue)));
  const dp = clampDecimalPlaces(Math.max(3, -magnitude + 2));
  return `${format(`.${dp}${removeTrailingZeroes ? '~' : ''}f`)(safeValue)}ms`;
}

// Per-value formatters for tooltip use (standalone magnitude, no domain-wide scale).
const tooltipFormatters: Record<DataType, (value: number) => string> = {
  number: (v) => formatNumber(v),
  integer: formatInteger,
  money: (v) => formatMoney(v),
  percentage: (v) => formatPercentage(v * 100),
  time: (v) => formatDuration(v),
};

/**
 * Returns a per-value formatter for tooltip use.
 * Each value is formatted in its own magnitude (e.g. 5K, 5M) instead of a domain-wide scale,
 * so thousands show as "5K" not "0.005M" when the chart also has million-scale values.
 *
 * Use this for tooltips; use createFormatterForValues for axis ticks and table columns.
 */
export function getStandaloneFormatterForTooltip(dataType: DataType): (value: number) => string {
  return tooltipFormatters[dataType] ?? tooltipFormatters.number;
}

/**
 * Format a duration using a specific time unit (for domain-consistent axis labels
 * where all ticks must use the same unit regardless of magnitude).
 * Use createYAxisFormatterFromTicks rather than calling this directly.
 *
 * @param value  Duration in milliseconds
 * @param unit   The unit to use (determines divisor)
 * @param options
 */
export function formatDurationWithUnit(value: number, unit: TimeUnit, options: FormatOptions = {}): string {
  if (!isFinite(value)) {
    return String(value);
  }
  const safeValue = value < 0 ? 0 : value;
  const { removeTrailingZeroes = false } = options;
  const converted = safeValue / getTimeUnitDivisor(unit);

  if (unit === 'ms') {
    return `${format(',.0f')(converted)}ms`;
  }

  const fmt = removeTrailingZeroes ? format('.1~f') : format('.1f');
  return `${fmt(converted)}${unit}`;
}

// =============================================================================
// 4. DATE / TIMESTAMP FORMATTERS
// =============================================================================

/**
 * Short date label (for X-axis tick context where space is limited).
 *
 * @example formatDateShort(timestamp) // "Jan 17"
 */
export function formatDateShort(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Date + time label, including the viewer's timezone (for tooltip context).
 *
 * @example formatDateTime(timestamp) // "Jan 17, 3:30 PM PST"
 */
export function formatDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Shows month+day at midnight, time-of-day otherwise.
 * Useful for X-axis labels over intraday data that spans multiple days.
 *
 * @example
 * formatTimestampMonthDay(midnightTs)   // "Jan 17"
 * formatTimestampMonthDay(afternoonTs)  // "3:30 PM"
 */
export function formatTimestampMonthDay(timestamp: number): string {
  const date = new Date(timestamp);
  const isMidnight = date.getHours() === 0 && date.getMinutes() === 0;
  return isMidnight
    ? date.toLocaleString('en-US', { month: 'short', day: 'numeric' })
    : date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// =============================================================================
// 5. X-AXIS TIME FORMATTER
// =============================================================================

const XAXIS_MS_PER_DAY = 24 * 60 * 60 * 1000;
const XAXIS_MS_PER_MONTH = 28 * XAXIS_MS_PER_DAY;

/**
 * Options for the X-axis time formatter.
 */
export interface XAxisTimeFormatterOptions {
  /** Inferred from data if not provided. */
  granularity?: TimeGranularity;
  /** Pass a consistent IANA timezone name (e.g. 'UTC') or leave undefined for local. */
  timeZone?: string;
}

/**
 * Infers time granularity from sorted timestamps using the minimum gap.
 * Falls back to 'daily' for a single value.
 */
export function computeTimeGranularity(sortedTimestamps: number[]): TimeGranularity {
  if (sortedTimestamps.length < 2) {
    return 'daily';
  }

  let minGap = Infinity;
  for (let i = 1; i < sortedTimestamps.length; i++) {
    const gap = sortedTimestamps[i] - sortedTimestamps[i - 1];
    if (gap > 0 && gap < minGap) {
      minGap = gap;
    }
  }

  if (minGap === Infinity || minGap <= 0) {
    return 'daily';
  }
  if (minGap < XAXIS_MS_PER_DAY) {
    return 'hourly';
  }
  if (minGap < XAXIS_MS_PER_MONTH) {
    return 'daily';
  }
  return 'monthly';
}

/**
 * Creates a scale-adaptive X-axis formatter that adds context (day / month / year)
 * only at boundaries, keeping labels compact within a run.
 *
 * @param sortedTimestamps  Sorted array of visible X values (used for granularity inference and boundary detection)
 * @param options
 */
export function createXAxisTimeFormatter(
  sortedTimestamps: number[],
  options: XAxisTimeFormatterOptions = {},
): (value: number) => string {
  const { timeZone, granularity: gran = computeTimeGranularity(sortedTimestamps) } = options;
  const locale = 'en-US';

  const timeOnly = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone });
  const dateOnly = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone });
  const dateWithYear = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: '2-digit', timeZone });
  const monthOnly = new Intl.DateTimeFormat(locale, { month: 'short', timeZone });
  const monthWithYear = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit', timeZone });
  const dateTimeFull = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });
  const dateTimeNoYear = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });

  const partsFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone,
  });

  function dateParts(ts: number): { year: number; month: number; day: number } {
    let year = 0,
      month = 0,
      day = 0;
    for (const p of partsFormatter.formatToParts(ts)) {
      if (p.type === 'year') {
        year = parseInt(p.value, 10);
      }
      if (p.type === 'month') {
        month = parseInt(p.value, 10);
      }
      if (p.type === 'day') {
        day = parseInt(p.value, 10);
      }
    }
    return { year, month, day };
  }

  function findPrevTs(value: number): number | null {
    // Binary-search-approximate: find the timestamp in the array just before value
    let i = 0;
    while (i < sortedTimestamps.length && sortedTimestamps[i] < value) {
      i++;
    }
    if (i === 0) {
      return null;
    }
    const prev = sortedTimestamps[i - 1];
    const curr = sortedTimestamps[i];
    if (curr === undefined) {
      return prev;
    }
    return prev;
  }

  return (value: number): string => {
    if (typeof value !== 'number' || !isFinite(value)) {
      return String(value);
    }

    const prevTs = findPrevTs(value);
    const parts = dateParts(value);
    const prev = prevTs !== null ? dateParts(prevTs) : null;

    const yearChanged = prev !== null && parts.year !== prev.year;
    const monthChanged = prev !== null && (parts.month !== prev.month || yearChanged);
    const dayChanged = prev !== null && (parts.day !== prev.day || monthChanged);

    if (gran === 'hourly') {
      if (yearChanged) {
        return dateTimeFull.format(value);
      }
      if (dayChanged) {
        return dateTimeNoYear.format(value);
      }
      return timeOnly.format(value);
    }

    if (gran === 'daily') {
      return yearChanged ? dateWithYear.format(value) : dateOnly.format(value);
    }

    // monthly
    return yearChanged ? monthWithYear.format(value) : monthOnly.format(value);
  };
}

// =============================================================================
// 6. AXIS TICK HELPERS
// =============================================================================

// Internal pre-configured formatters for each data type on Y-axes.
// Not exported directly; callers should use getYAxisFormatter or createYAxisFormatterFromTicks.
const yAxisFormatters = {
  number: (value: number) => formatNumber(value, AXIS_TICK_OPTIONS),
  integer: (value: number) => formatIntegerWithScale(value, null),
  money: (value: number) => formatMoney(value, AXIS_TICK_OPTIONS),
  // Percentage: standalone formatter without domain context.
  // createYAxisFormatterFromTicks is preferred when you have tick values.
  percentage: (value: number) => formatPercentage(value * 100, AXIS_TICK_OPTIONS),
  time: (value: number) => formatDuration(value, AXIS_TICK_OPTIONS),
} as const;

// No-scale fallback when createYAxisFormatterFromTicks has no ticks (unknown domain).
const yAxisFallbackFormatters: Record<DataType, (value: number) => string> = {
  number: (v) => formatNumberWithScale(v, null, AXIS_TICK_OPTIONS),
  integer: (v) => formatIntegerWithScale(v, null),
  money: (v) => formatMoney(v, AXIS_TICK_OPTIONS),
  percentage: (v) => formatPercentage(v * 100, AXIS_TICK_OPTIONS),
  time: (v) => formatDuration(v, AXIS_TICK_OPTIONS),
};

/**
 * Returns the default Y-axis formatter for a given data type.
 * Prefer createYAxisFormatterFromTicks when you have tick values — it produces
 * better consistency (same scale/unit/decimals across all ticks).
 */
export function getYAxisFormatter(dataType?: DataType): ((value: number) => string) | undefined {
  if (!dataType) {
    return undefined;
  }
  return yAxisFormatters[dataType as keyof typeof yAxisFormatters];
}

/**
 * Computes the minimum decimal places needed so that all tick values, when divided
 * by `divisor` (a K/M/B scale factor), produce distinct formatted strings.
 *
 * With 1 decimal place, step sizes < 0.1 in scaled space cause collisions.
 * E.g. ticks [1750, 1800] with K divisor → scaled step 0.05 → both round to "1.8".
 * Returning 2 lets the formatter distinguish them as "1.75" vs "1.8".
 */
function computeMinDecimalPlacesForTicks(ticks: number[], divisor: number): number {
  if (ticks.length < 2) {
    return 1;
  }
  let minStep = Infinity;
  for (let i = 1; i < ticks.length; i++) {
    const step = Math.abs(ticks[i] - ticks[i - 1]) / divisor;
    if (step > 0 && step < minStep) {
      minStep = step;
    }
  }
  if (!isFinite(minStep) || minStep <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(-Math.log10(minStep)));
}

/**
 * Formats a pre-scaled value (already divided by K/M/B divisor) to `dp` decimal
 * places with trailing zeros removed, appending the scale suffix.
 * Falls back to 3 significant figures when the value rounds to zero at `dp` places.
 */
function formatScaledValue(scaled: number, scale: NonNullable<Scale>, dp: number): string {
  if (scaled === 0) {
    return '0';
  }
  const formatted = format(`.${dp}~f`)(scaled);
  return isFormattedZero(formatted) ? `${format('.3~g')(scaled)}${scale}` : `${formatted}${scale}`;
}

/**
 * Creates a Y-axis formatter scoped to a specific set of tick values.
 * All ticks will use the same scale (K/M/B for numbers), the same time unit,
 * and for percentages the same number of decimal places.
 *
 * This is the preferred way to format Y-axis labels — call it once with your
 * tick array and reuse the returned function for each tick.
 *
 * Values are expected in the same range as the data type:
 * - number/integer/money: raw values (e.g. 1500, not "1.5K")
 * - percentage: 0-1 decimal (e.g. 0.857, not 85.7)
 * - time: milliseconds
 *
 * @example
 * const fmt = createYAxisFormatterFromTicks([0, 500, 1000, 2000], 'number');
 * fmt(500) // "0.5K"  (K because max 2000 > 1500 threshold)
 */
export function createYAxisFormatterFromTicks(
  ticks: number[],
  dataType: DataType = 'number',
): (value: number) => string {
  // No ticks: unknown domain — return a no-scale fallback so we don't incorrectly abbreviate.
  // For example, if max value turns out to be 1000 we should show "1,000" not "1K".
  if (ticks.length === 0) {
    return yAxisFallbackFormatters[dataType];
  }

  const maxValue = Math.max(...ticks.map(Math.abs));
  const scale = getScaleForDomain(maxValue);

  switch (dataType) {
    case 'number': {
      const divisor = scale !== null ? getScaleDivisor(scale) : 1;
      const dp = computeMinDecimalPlacesForTicks(ticks, divisor);
      if (dp > 1) {
        return scale !== null
          ? (v) => (isFinite(v) ? formatScaledValue(v / divisor, scale, dp) : String(v))
          : (v) => (!isFinite(v) ? String(v) : format(`,.${dp}~f`)(v));
      }
      return (v) => formatNumberWithScale(v, scale, AXIS_TICK_OPTIONS);
    }

    case 'money': {
      const symbol = getCurrencySymbol();
      const divisor = scale !== null ? getScaleDivisor(scale) : 1;
      // Scaled fallback uses 1dp; unscaled uses 2dp — only upgrade when the step is finer.
      const defaultDp = scale !== null ? 1 : 2;
      const dp = computeMinDecimalPlacesForTicks(ticks, divisor);
      if (dp > defaultDp) {
        return (v) => {
          if (!isFinite(v)) {
            return String(v);
          }
          const formatted =
            scale !== null ? formatScaledValue(Math.abs(v) / divisor, scale, dp) : format(`,.${dp}~f`)(Math.abs(v));
          return `${formatSign(v)}${symbol}${formatted}`;
        };
      }
      return (v) => formatMoneyWithScale(v, scale, { ...AXIS_TICK_OPTIONS, symbol });
    }

    case 'integer': {
      if (scale !== null) {
        const divisor = getScaleDivisor(scale);
        const dp = computeMinDecimalPlacesForTicks(ticks, divisor);
        if (dp > 1) {
          return (v) => (isFinite(v) ? formatScaledValue(v / divisor, scale, dp) : String(v));
        }
      }
      return (v) => formatIntegerWithScale(v, scale);
    }

    case 'percentage': {
      // Convert ticks from 0-1 to 0-100 range to check decimals
      const pctTicks = ticks.map((t) => t * 100);
      const dp = computeMinDecimalPlacesForTicks(pctTicks, 1);
      if (dp > 1) {
        // Ticks are too close for 1 decimal place — use enough decimals to distinguish them.
        return (v) => {
          if (!isFinite(v)) {
            return String(v);
          }
          return `${format(`.${dp}~f`)(v * 100)}%`;
        };
      }
      const forceDecimals = hasAnyNonZeroDecimal(pctTicks);
      return (v) => (forceDecimals ? formatPercentageFixed1(v * 100) : formatPercentage(v * 100, AXIS_TICK_OPTIONS));
    }

    case 'time': {
      const unit = getTimeUnitForDomain(maxValue);
      // 'ms' uses integer formatting (no decimals), so duplicates can't occur from rounding.
      // For other units (s/m/h/d/y), use extra decimal places when the tick step is too fine.
      if (unit !== 'ms') {
        const divisor = getTimeUnitDivisor(unit);
        const dp = computeMinDecimalPlacesForTicks(ticks, divisor);
        if (dp > 1) {
          return (v) => {
            if (!isFinite(v)) {
              return String(v);
            }
            const safeValue = v < 0 ? 0 : v;
            return `${format(`.${dp}~f`)(safeValue / divisor)}${unit}`;
          };
        }
      }
      return (v) => formatDurationWithUnit(v, unit, AXIS_TICK_OPTIONS);
    }

    default:
      return (v) => formatNumberWithScale(v, scale, AXIS_TICK_OPTIONS);
  }
}

// =============================================================================
// 7. DOMAIN-AWARE FACTORY
// =============================================================================

/**
 * Creates a formatter for a set of related values that will all be displayed
 * together (e.g. all rows in a table column, all data points in a chart series).
 *
 * Auto-detects the right scale / unit / decimal places from the data so that
 * every value in the set is formatted consistently — no manual configuration
 * needed or expected.
 *
 * Rules by data type:
 * - 'number'     Uses K/M/B scale when max > 1500; 1 decimal place for alignment.
 * - 'integer'    Uses K/M/B scale when max > 1500; no decimals.
 * - 'money'      Column precision = decimal places from the smallest value (3 sig figs,
 *                trailing zeros removed). All values pad to that precision for alignment;
 *                never have every number end in zero. Abbreviates values >= $1000.
 * - 'percentage' Shows 0 decimals if all values are whole-number percentages,
 *                1 decimal if any value has a fractional part. Both cases keep
 *                all values aligned.
 * - 'time'       Picks a single unit (ms/s/m/h/d/y) based on the max value
 *                so all times in the set use the same unit.
 *
 * @param values   All values in the set (nulls are ignored for scale detection)
 * @param dataType The semantic type of the data
 * @param options  Optional overrides. Pass `fixedDecimals` to bypass all auto-detection and
 *                 force an exact number of decimal places on every value. Pass `currencyCode`
 *                 to use a non-USD currency symbol in money formatting.
 *
 * @example
 * // Table column with mix of small and large token counts:
 * const fmt = createFormatterForValues([512, null, 33600, 32500], 'number');
 * fmt(512)    // "0.5K"  (K because max 33600 > 1500)
 * fmt(33600)  // "33.6K"
 *
 * // Satisfaction column with mixed decimals:
 * const fmt = createFormatterForValues([0.75, 0.857, 1.0], 'percentage');
 * fmt(0.75)   // "75.0%"  (forced decimal because 0.857 → 85.7%)
 * fmt(0.857)  // "85.7%"
 * fmt(1.0)    // "100.0%"
 *
 * // Fixed-precision money column in Euros:
 * const fmt = createFormatterForValues([0.5, 1.23, 100], 'money', { fixedDecimals: 2, currencyCode: 'EUR' });
 * fmt(0.5)   // "€0.50"
 * fmt(100)   // "€100.00"
 */
export function createFormatterForValues(
  values: Array<number | null | undefined>,
  dataType: DataType,
  options?: FormatOptions,
): (value: number) => string {
  const { fixedDecimals, currencyCode } = options ?? {};
  const nums = values.filter((v): v is number => v !== null && v !== undefined && !isNaN(v));

  // When fixedDecimals is set, bypass all auto-detection and return a fixed-precision formatter.
  // When value would round to zero, show "< 0.01" (or equivalent) instead.
  if (fixedDecimals !== undefined) {
    const dp = clampDecimalPlaces(fixedDecimals);
    switch (dataType) {
      case 'money': {
        const symbol = getCurrencySymbol(currencyCode);
        const minStr = Math.pow(10, -dp).toFixed(dp);
        const nf = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode ?? 'USD',
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: dp,
          maximumFractionDigits: dp,
        });
        return (v) => {
          if (!isFinite(v)) {
            return String(v);
          }
          if (isBelowMinimumRepresentable(v, fixedDecimals)) {
            return v < 0 ? `> ${formatSign(v)}${symbol}${minStr}` : `< ${symbol}${minStr}`;
          }
          return nf.format(v);
        };
      }
      case 'percentage': {
        const minStr = Math.pow(10, -dp).toFixed(dp);
        return (v) => {
          if (!isFinite(v)) {
            return String(v);
          }
          const pct = v * 100;
          if (isBelowMinimumRepresentable(pct, fixedDecimals)) {
            return v < 0 ? `> ${formatSign(pct)}${minStr}%` : `< ${minStr}%`;
          }
          return `${format(`.${dp}f`)(pct)}%`;
        };
      }
      case 'integer':
        // fixedDecimals doesn't apply to integers — always output a whole number.
        return (v) => (!isFinite(v) ? String(v) : format(',d')(Math.round(v)));
      case 'time': {
        const maxValue = nums.length > 0 ? Math.max(...nums.map(Math.abs)) : 0;
        const unit = getTimeUnitForDomain(maxValue);
        const divisor = getTimeUnitDivisor(unit);
        if (unit === 'ms') {
          const minStr = Math.pow(10, -dp).toFixed(dp);
          return (v) => {
            if (!isFinite(v)) {
              return String(v);
            }
            if (isBelowMinimumRepresentable(v, fixedDecimals)) {
              return v < 0 ? `> ${formatSign(v)}${minStr}ms` : `< ${minStr}ms`;
            }
            return `${format(`,.${dp}f`)(v)}ms`;
          };
        }
        const minStr = Math.pow(10, -dp).toFixed(dp);
        return (v) => {
          if (!isFinite(v)) {
            return String(v);
          }
          const converted = v / divisor;
          if (isBelowMinimumRepresentable(converted, fixedDecimals)) {
            return v < 0 ? `> ${formatSign(converted)}${minStr}${unit}` : `< ${minStr}${unit}`;
          }
          return `${format(`,.${dp}f`)(converted)}${unit}`;
        };
      }
      default: {
        // 'number'
        const minStr = Math.pow(10, -dp).toFixed(dp);
        return (v) => {
          if (!isFinite(v)) {
            return String(v);
          }
          if (isBelowMinimumRepresentable(v, fixedDecimals)) {
            return v < 0 ? `> ${formatSign(v)}${minStr}` : `< ${minStr}`;
          }
          return format(`,.${dp}f`)(v);
        };
      }
    }
  }

  // No valid data — return the standalone formatter as a safe default.
  // When options are provided we pass them through; otherwise use tooltipFormatters.
  if (nums.length === 0) {
    const opts = options ?? {};
    if (
      opts.currencyCode === undefined &&
      opts.fixedDecimals === undefined &&
      opts.removeTrailingZeroes === undefined &&
      opts.useSciNotationForSmallValues === undefined
    ) {
      return tooltipFormatters[dataType];
    }
    switch (dataType) {
      case 'money':
        return (v) => formatMoney(v, opts);
      case 'percentage':
        return (v) => formatPercentage(v * 100, opts);
      case 'time':
        return (v) => formatDuration(v, opts);
      case 'integer':
        return formatInteger;
      default:
        return (v) => formatNumber(v, opts);
    }
  }

  const maxValue = Math.max(...nums.map(Math.abs));
  const scale = getScaleForDomain(maxValue);

  switch (dataType) {
    case 'number':
      // 1 decimal place for alignment across the column. K/M/B applies when max > 1500.
      return (v) => formatNumberWithScale(v, scale);

    case 'integer':
      return (v) => formatIntegerWithScale(v, scale);

    case 'money': {
      const symbol = getCurrencySymbol(currencyCode);
      const optsWithSymbol = { ...options, symbol };
      if (maxValue >= 1000) {
        // K/M/B territory — apply domain-wide scale so the column reads consistently
        // (e.g. $0.5K, $1.5K, $5.0K rather than mixing $500 with $1.50K).
        // Note: getScaleForDomain returns K only when max > 1500; for 1000–1500 we
        // still want abbreviation (e.g. $1.0K not $1,000.00), so fall back to 'K'.
        const moneyScale = scale ?? 'K';
        return (v) => formatMoneyWithScale(v, moneyScale, optsWithSymbol);
      }
      // Small values — use the smallest-value rule: decimal places come from the smallest
      // value (3 sig figs); all values pad to that precision for alignment.
      const dp = decimalPrecisionForSet(values);
      return (v) => formatMoneyFixed(v, dp, symbol);
    }

    case 'percentage': {
      // Auto-detect: if any value has a fractional percentage, all values
      // show 1 decimal so the column stays visually aligned.
      const pctValues = nums.map((v) => v * 100);
      const forceDecimals = hasAnyNonZeroDecimal(pctValues);
      return forceDecimals ? (v) => formatPercentageFixed1(v * 100) : (v) => formatPercentage(v * 100);
    }

    case 'time': {
      const unit = getTimeUnitForDomain(maxValue);
      return (v) => formatDurationWithUnit(v, unit);
    }

    default:
      return (v) => formatNumberWithScale(v, scale);
  }
}
